import { clearCart } from "../../redux/slices/cartSlice";
import {
  getOrderIdFromResponse,
  getOrderNumberFromResponse,
} from "../../utils/checkoutCartOrderLink";

export function getCheckoutPaymentMetaFromOrder(
  order: Record<string, unknown> | null | undefined,
  fallbacks: { payerEmail?: string; paidByName?: string } = {}
): {
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
} {
  const data = (order ?? {}) as Record<string, unknown>;
  const first = String(data.customer_first_name ?? "").trim();
  const last = String(data.customer_last_name ?? "").trim();
  const nameFromOrder = [first, last].filter(Boolean).join(" ");
  const paidByName = String(fallbacks.paidByName ?? "").trim();
  const payerEmail = String(fallbacks.payerEmail ?? "").trim();

  return {
    orderNumber: getOrderNumberFromResponse(order),
    customerName: nameFromOrder || paidByName,
    customerPhone: String(data.customer_phone ?? "").trim(),
    customerEmail: String(data.customer_email ?? payerEmail).trim() || payerEmail,
  };
}

export type CheckoutPaymentStatus = "Paid" | "Pending";

export type CheckoutPaymentPutBody = {
  payment_id: string;
  method: string;
  total_amount: number;
  amount_paid: number;
  amount_due: number;
  payment_date: string;
  payment_status: CheckoutPaymentStatus;
  paid_by?: { id: string; name: string };
};

/** @deprecated Use CheckoutPaymentPutBody */
export type CheckoutPaymentInfoRow = CheckoutPaymentPutBody;

const round2 = (n: number) => Math.round(Number(n || 0) * 100) / 100;

export function buildCheckoutPaymentInfoRow(params: {
  method: string;
  totalAmount: number;
  amountPaid: number;
  paymentStatus: CheckoutPaymentStatus;
  paymentId?: string;
  paidBy?: { id: string; name: string };
}): CheckoutPaymentPutBody {
  const total = round2(params.totalAmount);
  const paid = round2(params.amountPaid);
  return {
    payment_id: String(params.paymentId || ""),
    method: params.method,
    total_amount: total,
    amount_paid: paid,
    amount_due: round2(Math.max(0, total - paid)),
    payment_date: new Date().toISOString(),
    payment_status: params.paymentStatus,
    ...(params.paidBy ? { paid_by: params.paidBy } : {}),
  };
}

/** PUT /orders/{id}/payment after checkout payment succeeds. */
export async function completeCheckoutPayment(params: {
  orderId: string;
  token: string;
  paymentBody: CheckoutPaymentPutBody;
}): Promise<unknown> {
  const orderId = String(params.orderId || "").trim();
  if (!orderId || !params.token) {
    throw new Error("Order id and token are required to record payment.");
  }

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/orders/${orderId}/payment`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${params.token}`,
      },
      body: JSON.stringify(params.paymentBody),
    }
  );

  const raw = await response.text();
  let json: unknown = {};
  try {
    json = raw ? JSON.parse(raw) : {};
  } catch {
    json = { raw };
  }

  if (!response.ok) {
    const message =
      (json as { message?: string; error?: string })?.message ||
      (json as { message?: string; error?: string })?.error ||
      raw ||
      "Failed to update order payment";
    throw new Error(message);
  }

  return json;
}

export function resolveCheckoutOrderId(order: unknown): string {
  return getOrderIdFromResponse(order);
}

type CartClearDeps = {
  dispatch: (action: unknown) => void;
  clearCartItems: () => void;
  fetchCart: () => Promise<void> | void;
};

/** Clear cart storage after successful checkout payment. */
export function clearCheckoutCartAfterPayment(deps: CartClearDeps): void {
  localStorage.removeItem("cart_id");
  deps.dispatch(clearCart());
  deps.clearCartItems();
  void deps.fetchCart();
}
